import * as React from "react"
import { cn } from "../../lib/utils"

export interface TextareaWithIconProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  label?: string
  description?: string
  error?: string
  rows?: number
  maxRows?: number
}

const TextareaWithIcon = React.forwardRef<HTMLTextAreaElement, TextareaWithIconProps>(
  ({ 
    className, 
    leftIcon, 
    rightIcon, 
    label, 
    description, 
    error, 
    id,
    rows = 3,
    maxRows = 10,
    onChange,
    ...props 
  }, ref) => {
    const [textareaRows, setTextareaRows] = React.useState(rows)
    const inputId = id || React.useId()

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = e.target
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight)
      const scrollHeight = textarea.scrollHeight
      const newRows = Math.min(Math.max(rows, Math.ceil(scrollHeight / lineHeight)), maxRows)
      
      setTextareaRows(newRows)
      
      // Call the original onChange if provided
      if (onChange) {
        onChange(e)
      }
    }

    const textareaElement = (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-3 text-muted-foreground pointer-events-none z-10">
            {leftIcon}
          </div>
        )}
        {rightIcon && (
          <div className="absolute right-3 top-3 text-muted-foreground pointer-events-none z-10">
            {rightIcon}
          </div>
        )}
        <textarea
          id={inputId}
          className={cn(
            "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden",
            leftIcon && "pl-10",
            rightIcon && "pr-10",
            className
          )}
          rows={textareaRows}
          ref={ref}
          onChange={handleTextareaChange}
          aria-describedby={description ? `${inputId}-description` : undefined}
          aria-invalid={error ? true : undefined}
          {...props}
        />
      </div>
    )

    if (label || description || error) {
      return (
        <div className="space-y-2">
          {label && (
            <label htmlFor={inputId} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">
              {label}
            </label>
          )}
          {textareaElement}
          {description && (
            <p id={`${inputId}-description`} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
          {error && (
            <p className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      )
    }

    return textareaElement
  }
)
TextareaWithIcon.displayName = "TextareaWithIcon"

export { TextareaWithIcon } 